import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {
  ACCOUNT_SELF_LOGO,
  DELIVERY_PROOF,
  FIELD_TO_INCLUDE,
  GET_CARRIER_DATA,
  GET_DELIVERY_DETAILS,
  GET_ETA,
  GET_ORDER_STATUS,
  GET_TRACK_DETAILS,
  SUBMIT_USER_RATING,
  GET_FEATURE_VISBILITY_PROPERTIES,
  GET_ORDER_TRACK_INFO,
  GET_ORDER_DATA_INFO,
  GET_ORDER_DATA_INFO_GRAPHQL,
  GET_ORDER_TRACK_INFO_CONFIG_GRAPHQL,
  GET_ACCOUNT_LOGO_GRAPHQL
} from "./constants";
import {getApiBaseUrl} from "../config/api";
import {
  DeliveryDetails,
  IETADetails,
  IFeatureVisbilityProperties,
  IOrderStatus,
  IOrderTrackByIdResponse,
  IRatingDetails,
  IRatingPayload,
  TrackDetails
} from "../models/DeliveryDetails";
import {IDeliveryProofPayload} from "../components/delivery-proof/delivery-proof.component";
import {CarrierData} from "src/app/models/custom.types";
import { Observable, of } from "rxjs";
import { LatLng } from "leaflet";
import { CustomerDetails, IOrderConfig, OrdersById } from "../components/calibrate-data/calibrate-data.component";
import { IRootOrder, IStatusChecked, RootDeliveryStatusInterface } from "../components/warehouse-scan/warehouse-scan.component";

export interface AccountSelfLogoResponse {
  images?: {
    logo?: {
      data?: string;
      contentType?: string;
      url?: string;
    }
  }
}

@Injectable({providedIn: 'root'})
export class ApiService {

  constructor(private http: HttpClient) {
  }


  getCarrierData() {
    const url = `${getApiBaseUrl()}${GET_CARRIER_DATA}`
    return this.http.get<CarrierData>(url);
  }

  getDeliveryDetails() {
    const url = `${getApiBaseUrl()}${GET_DELIVERY_DETAILS}`
    return this.http.get<DeliveryDetails>(url);
  }

  // Same endpoint as getDeliveryDetails, but the interceptor appends ?orderId=
  // instead of ?deliveryTokenId=, and the backend returns a different shape.
  getOrderTrackById() {
    const url = `${getApiBaseUrl()}${GET_DELIVERY_DETAILS}`
    return this.http.get<IOrderTrackByIdResponse>(url);
  }

  getTrackDetails() {
    const url = `${getApiBaseUrl()}${GET_TRACK_DETAILS}`;
    return this.http.get<TrackDetails[]>(url);
  }

  getEta() {
    const url = `${getApiBaseUrl()}${GET_ETA}`;
    return this.http.get<IETADetails>(url);
  }

  getRatings() {
    let url = `${getApiBaseUrl()}${SUBMIT_USER_RATING}?fI=${FIELD_TO_INCLUDE}`

    return this.http.get<IRatingDetails[]>(url);
  }

  getOderStatus() {
    const url = `${getApiBaseUrl()}${GET_ORDER_STATUS}`;
    return this.http.get<IOrderStatus>(url);
  }

  postRatings(payload: IRatingPayload[]) {
    let url = `${getApiBaseUrl()}${SUBMIT_USER_RATING}`
    return this.http.post<IRatingPayload[]>(url, payload);
  }

  private dataURLtoBlob(dataURL: string) {
    const parts = dataURL.split(';base64,');
    const contentType = parts[0].split(':')[1];
    const raw = window.atob(parts[1]);
    const rawLength = raw.length;
    const uInt8Array = new Uint8Array(rawLength);

    for (let i = 0; i < rawLength; ++i) {
      uInt8Array[i] = raw.charCodeAt(i);
    }

    return new Blob([uInt8Array], {type: contentType});
  }

  postDeliveryConfirmation(payload: IDeliveryProofPayload, deliveryStatus: unknown, _?: string) {
    let url = `${getApiBaseUrl()}${DELIVERY_PROOF}${deliveryStatus}`

    const formData = new FormData();
    formData.append('name', payload.name);
    formData.append('mobileNo', payload.mobileNo);
    formData.append('reason', payload.reason);

    if (payload?.receivedDate) {
      formData.append('receivedDate', payload.receivedDate);
    }
    if (payload?.file) {
      let file = this.dataURLtoBlob(payload.file);
      formData.append('file', file, 'filename.png');
    }
    if (payload?.sign) {
      let file = this.dataURLtoBlob(payload.sign);
      formData.append('sign', file, 'sign.png');
    }

    return this.http.post<{ msg: string }>(url, formData)
  }

  getFeatureVisbilityProperties() {
    const url = `${getApiBaseUrl()}${GET_FEATURE_VISBILITY_PROPERTIES}`
    return this.http.get<IFeatureVisbilityProperties>(url);
  }

  getOrderDeliveryStatus() {
    const url = `${getApiBaseUrl()}/api/wms/customer/order/statusesAllowed `
    return this.http.get<RootDeliveryStatusInterface[]>(url);
  }

  getOrderDataForBatches(id:string) {
    const url = `${getApiBaseUrl()}/api/wms/customer/order/`+id
    return this.http.get<IRootOrder>(url);
  }
  updateOrderStatus(id:string,status:string) {
    const url = `${getApiBaseUrl()}/api/wms/customer/order/status/${id}/${status}`
    return this.http.put<any>(url,{});
  }
  updateSerialNumbersStatus(serialNumberArray:string[],status:string){
    const url = `${getApiBaseUrl()}/api/wms/customer/check/serialNumbers/status/${status}`
    return this.http.put(url,{serialNumberIds:serialNumberArray})
  }
  getOrderTrackInfoConfig(orderId:string,accountId:string){
    const url = `${getApiBaseUrl()}${GET_ORDER_TRACK_INFO_CONFIG_GRAPHQL}?accountId=${accountId}`
    return this.http.post<IOrderConfig>(url,{})
  }

  getOrderDeatilsData(){
    let url = `${getApiBaseUrl()}${GET_ORDER_DATA_INFO_GRAPHQL}`
    return this.http.post<{data:{ordersById:OrdersById}}>(url,{})
  }

  updateOrderDetailsAddress(payload:{customerDetails:CustomerDetails}){
    let url = `${getApiBaseUrl()}${GET_ORDER_DATA_INFO}`
    return this.http.put<{msg: string}>(url,payload)
  }

  getAccountLogo(orderId:string,accountId:string){
    const url = `${getApiBaseUrl()}${GET_ACCOUNT_LOGO_GRAPHQL}?accountId=${accountId}`
    return this.http.post(url,{})
  }

  getSelfAccountLogo() {
    const url = `${getApiBaseUrl()}${ACCOUNT_SELF_LOGO}`
    return this.http.get<AccountSelfLogoResponse>(url)
  }

  reverseGeoCoding(event:LatLng): Observable<any> {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${event.lat},${event.lng}&key=AIzaSyAXV6COhVSE0E_EfbtG7OKVmFMeghkI8To`;

    return this.http.get<GeocodeResp>(url);
  }

  getStatuses(id:string) {
    const url = `${getApiBaseUrl()}/api/wms/customer/order/statusesAllowed/orderId/${id}`
    return this.http.get<any>(url);
  }
}

export type GeocodeResp = {
  plus_code: {
    compound_code: string
    global_code: string
  }
  results: Array<{
    address_components: Array<{
      long_name: string
      short_name: string
      types: Array<string>
    }>
    formatted_address: string
    geometry: {
      bounds: {
        northeast: {
          lat: number
          lng: number
        }
        southwest: {
          lat: number
          lng: number
        }
      }
      location: {
        lat: number
        lng: number
      }
      location_type: string
      viewport: {
        northeast: {
          lat: number
          lng: number
        }
        southwest: {
          lat: number
          lng: number
        }
      }
    }
    place_id: string
    plus_code?: {
      compound_code: string
      global_code: string
    }
    types: Array<string>
  }>
  status: string
}
